import React from "react";

import Skeleton, { SkeletonRegion } from "../../../components/Skeleton";

export const SupplierRowSkeleton = () => (
  <li role="none" className="sy-supplier-row is-skeleton">
    <span className="sy-supplier-main">
      <span className="sy-supplier-pick" />

      <Skeleton variant="avatar" width="44px" height="44px" radius="var(--radius-sm)" />

      <span className="sy-supplier-body">
        <span className="sy-supplier-namerow">
          <Skeleton variant="text" width="62%" height="15px" />
        </span>

        <span className="sy-supplier-meta">
          <Skeleton variant="text" width="64px" height="12px" />
          <Skeleton variant="text" width="88px" height="12px" />
        </span>
      </span>

      <span className="sy-price sy-price-right">
        <Skeleton variant="text" width="72px" height="15px" />
        <Skeleton variant="text" width="54px" height="10px" />
      </span>
    </span>

    <span className="sy-supplier-info">
      <Skeleton variant="circle" width="12px" height="12px" />
    </span>
  </li>
);

export const SupplierRowsSkeleton = ({ rows = 4 }) => (
  <SkeletonRegion label="Loading suppliers">
    <ul className="sy-supplier-list">
      {Array.from({ length: rows }, (_, index) => (
        <SupplierRowSkeleton key={`supplier-skeleton-${index}`} />
      ))}
    </ul>
  </SkeletonRegion>
);

export const BuyBoxSkeleton = () => (
  <SkeletonRegion label="Loading best offer">
    <div className="sy-buybox is-skeleton">
      <div className="sy-buybox-head">
        <Skeleton variant="avatar" width="44px" height="44px" radius="var(--radius-sm)" />

        <div className="sy-buybox-identity sy-skel-stack">
          <Skeleton variant="text" width="96px" height="11px" />
          <Skeleton variant="text" width="58%" height="16px" />
        </div>
      </div>

      <div className="sy-buybox-price">
        <Skeleton variant="title" width="170px" height="30px" />
      </div>

      <div className="sy-buybox-actions">
        <Skeleton variant="text" width="118px" height="46px" radius="var(--radius-md)" />
        <Skeleton variant="text" width="150px" height="46px" radius="var(--radius-md)" />
        <Skeleton variant="text" width="150px" height="46px" radius="var(--radius-md)" />
      </div>
    </div>
  </SkeletonRegion>
);

export const ProductCardSkeleton = () => (
  <li className="sy-grid-card is-skeleton">
    <span>
      <span className="sy-grid-media">
        <Skeleton variant="block" />
      </span>

      <span className="sy-grid-name">
        <Skeleton variant="text" width="82%" height="12px" />
        <Skeleton variant="text" width="54%" height="12px" />
      </span>

      <span className="sy-price">
        <Skeleton variant="text" width="64px" height="15px" />
      </span>
    </span>
  </li>
);

export const ProductGridSkeleton = ({ cards = 6 }) => (
  <SkeletonRegion label="Loading products">
    <ul className="sy-product-grid">
      {Array.from({ length: cards }, (_, index) => (
        <ProductCardSkeleton key={`product-skeleton-${index}`} />
      ))}
    </ul>
  </SkeletonRegion>
);

export const AccessoryRailSkeleton = ({ cards = 4 }) => (
  <SkeletonRegion label="Loading accessories">
    <div className="sy-rail-section">
      <Skeleton variant="title" width="140px" />

      <ul className="sy-product-grid">
        {Array.from({ length: cards }, (_, index) => (
          <ProductCardSkeleton key={`accessory-skeleton-${index}`} />
        ))}
      </ul>
    </div>
  </SkeletonRegion>
);

export const CustomRefillFormSkeleton = () => (
  <SkeletonRegion label="Loading custom fill options">
    <div className="sy-refill-form is-skeleton">
      <div className="sy-refill-entry">
        <div className="sy-refill-grid">
          {["a", "b", "c", "d"].map((id) => (
            <div className="sy-refill-field" key={`refill-field-${id}`}>
              <Skeleton variant="text" width="70px" height="11px" />
              <Skeleton variant="text" width="100%" height="42px" radius="var(--radius-md)" />
            </div>
          ))}
        </div>

        <Skeleton variant="text" width="140px" height="16px" />
      </div>
    </div>
  </SkeletonRegion>
);

export const InfoCardSkeleton = () => (
  <SkeletonRegion label="Loading supplier">
    <div className="sy-store">
      <div className="sy-buy-head">
        <Skeleton variant="avatar" width="52px" height="52px" radius="var(--radius-md)" />

        <div className="sy-store-heading sy-skel-stack">
          <Skeleton variant="title" width="62%" height="22px" />
          <Skeleton variant="text" width="120px" />
          <Skeleton variant="text" width="72%" />
        </div>
      </div>

      <div className="sy-chip-row">
        <Skeleton variant="text" width="86px" height="24px" radius="var(--radius-pill)" />
        <Skeleton variant="text" width="132px" height="24px" radius="var(--radius-pill)" />
      </div>

      <div className="sy-map-card">
        <Skeleton variant="block" height="100%" />
      </div>
    </div>
  </SkeletonRegion>
);

export const TabStripSkeleton = () => (
  <SkeletonRegion label="Loading purchase types">
    <div className="sy-tabstrip">
      {["a", "b", "c"].map((id) => (
        <Skeleton
          key={`tab-skeleton-${id}`}
          variant="text"
          width="92px"
          height="34px"
          radius="var(--radius-pill)"
        />
      ))}
    </div>
  </SkeletonRegion>
);

export const ProductBodySkeleton = () => (
  <SkeletonRegion label="Loading product">
    <div className="sy-buy">
      <div className="sy-buy-head">
        <Skeleton variant="title" width="74%" height="26px" />
      </div>

      <div className="sy-chip-row">
        <Skeleton variant="text" width="92px" height="24px" radius="var(--radius-pill)" />
        <Skeleton variant="text" width="64px" height="24px" radius="var(--radius-pill)" />
      </div>

      <div className="sy-buy-price">
        <Skeleton variant="title" width="160px" height="32px" />
      </div>

      <div className="sy-buy-actions">
        <Skeleton variant="text" width="118px" height="44px" radius="var(--radius-md)" />
        <Skeleton variant="text" width="160px" height="44px" radius="var(--radius-md)" />
      </div>

      <div className="sy-seller">
        <Skeleton variant="avatar" width="44px" height="44px" />

        <div className="sy-seller-body sy-skel-stack">
          <Skeleton variant="text" width="38%" />
          <Skeleton variant="text" width="60%" />
        </div>
      </div>
    </div>
  </SkeletonRegion>
);

export const ReviewsSkeleton = ({ rows = 3 }) => (
  <SkeletonRegion label="Loading reviews">
    <div className="sy-skel-stack" style={{ gap: "18px" }}>
      {Array.from({ length: rows }, (_, index) => (
        <div className="sy-skel-stack" key={`review-skeleton-${index}`}>
          <Skeleton variant="text" width="46%" />
          <Skeleton variant="text" width="30%" />
          <Skeleton variant="text" />
        </div>
      ))}
    </div>
  </SkeletonRegion>
);
